import { api } from './api';
import { isNative } from './platform';
import { PORTAL } from './portal';

/**
 * Web Push. The service worker (public/sw.js) only shows notifications. Native
 * apps need FCM/APNs instead, so push is web-only for now.
 */
export const pushSupported = () =>
  !isNative && !PORTAL && typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

let registration: Promise<ServiceWorkerRegistration | null> | null = null;

export function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!pushSupported()) return Promise.resolve(null);
  registration ??= navigator.serviceWorker.register('/sw.js').catch(() => null);
  return registration;
}

let vapidKey: Promise<string | null> | null = null;
const serverKey = () => (vapidKey ??= api<{ vapidPublicKey: string | null }>('/api/config', { auth: false }).then((c) => c.vapidPublicKey).catch(() => null));

function base64UrlToBytes(s: string): Uint8Array<ArrayBuffer> {
  const b64 = (s + '='.repeat((4 - (s.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export type PushState = 'unsupported' | 'unavailable' | 'denied' | 'off' | 'on';

/** 'unavailable' = the server has no VAPID keys configured. */
export async function pushState(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  if (!(await serverKey())) return 'unavailable';
  if (Notification.permission === 'denied') return 'denied';
  const reg = await registerServiceWorker();
  const sub = await reg?.pushManager.getSubscription();
  return sub && Notification.permission === 'granted' ? 'on' : 'off';
}

/** Ask permission (must be called from a user gesture) and register this browser. */
export async function enablePush(): Promise<PushState> {
  const key = await serverKey();
  const reg = await registerServiceWorker();
  if (!key || !reg) return pushState();
  if ((await Notification.requestPermission()) !== 'granted') return pushState();
  const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: base64UrlToBytes(key) }));
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  await api('/api/push/subscriptions', { body: { endpoint: json.endpoint, keys: json.keys } });
  return 'on';
}

export async function disablePush(): Promise<PushState> {
  const reg = await registerServiceWorker();
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await api('/api/push/subscriptions', { method: 'DELETE', body: { endpoint: sub.endpoint } }).catch(() => {});
    await sub.unsubscribe();
  }
  return pushState();
}
